"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "@/components/auth/Auth";
import { useSetWaveData } from "@/contexts/TitleContext";
import { type HeaderWaveDropAction, useHeaderContext } from "@/contexts/HeaderContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "./MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import MyStreamWavePolls from "./MyStreamWavePolls";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { useWaveEligibility } from "@/contexts/wave/WaveEligibilityContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { SubmissionStatus, useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getDropQueryKey } from "@/services/api/drop-api";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { selectEditingDropId } from "@/store/editSlice";
import type { ChatSubmitDropAction, ChatSubmitDropState } from "./chatSubmitDrop.types";
import { getChatSubmitDropLabels } from "./chatSubmitDrop.types";
export interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const getChatSubmitDropRestrictionMessage = ({
  dropEligibility,
  isApprovalVotingControlsLocked
}: {
  readonly dropEligibility: ReturnType<typeof getWaveDropEligibility>;
  readonly isApprovalVotingControlsLocked: boolean;
}): string | null => {
  if (!dropEligibility.canCreateDrop) {
    return dropEligibility.restrictionMessage;
  }
  if (isApprovalVotingControlsLocked) {
    return "Approval controls are locked";
  }
  return null;
};
type MemesHeaderDropActionState = Pick<HeaderWaveDropAction, "canOpen" | "label" | "compactLabel" | "restrictionMessage">;
interface MemesHeaderParticipationState {
  readonly canSubmitNow: boolean;
  readonly endTime: number;
  readonly hasReachedLimit: boolean;
  readonly isEligible: boolean;
  readonly maxSubmissions: number | null;
  readonly startTime: number;
  readonly status: SubmissionStatus;
}
const getMemesSubmissionPeriodHeaderDropActionState = ({
  endTime,
  startTime,
  status
}: Pick<MemesHeaderParticipationState, "endTime" | "startTime" | "status">): MemesHeaderDropActionState | null => {
  if (status === SubmissionStatus.ENDED) {
    const closingTime = endTime ? new Date(endTime).toLocaleString() : null;
    return {
      canOpen: false,
      label: "Submissions Closed",
      compactLabel: "Closed",
      restrictionMessage: closingTime ? `Submissions closed on ${closingTime}` : "Submissions are closed"
    };
  }
  if (status === SubmissionStatus.NOT_STARTED) {
    const openingTime = startTime ? new Date(startTime).toLocaleString() : null;
    return {
      canOpen: false,
      label: "Submissions Open Soon",
      compactLabel: "Opens",
      restrictionMessage: openingTime ? `Submissions open on ${openingTime}` : "Submissions will open soon"
    };
  }
  return null;
};
const getMemesHeaderDropActionState = ({
  participationState,
  isApprovalVotingControlsLocked
}: {
  readonly participationState: MemesHeaderParticipationState;
  readonly isApprovalVotingControlsLocked: boolean;
}): MemesHeaderDropActionState => {
  if (isApprovalVotingControlsLocked) {
    return {
      canOpen: false,
      label: "Submit Work to The Memes",
      compactLabel: "Submit",
      restrictionMessage: "Approval controls are locked"
    };
  }
  const periodActionState = getMemesSubmissionPeriodHeaderDropActionState(participationState);
  if (periodActionState) {
    return periodActionState;
  }
  if (!participationState.isEligible) {
    return {
      canOpen: false,
      label: "How to Submit",
      compactLabel: "Submit",
      restrictionMessage: "You are not eligible to submit to this wave"
    };
  }
  if (participationState.hasReachedLimit) {
    const maxSubmissions = participationState.maxSubmissions ?? "?";
    const submissionText = maxSubmissions === 1 ? "1 submission" : `${maxSubmissions} submissions`;
    return {
      canOpen: false,
      label: "Submission Limit Reached",
      compactLabel: "Limit",
      restrictionMessage: `You have already submitted the maximum allowed (${submissionText})`
    };
  }
  if (!participationState.canSubmitNow) {
    return {
      canOpen: false,
      label: "Submit Work to The Memes",
      compactLabel: "Submit",
      restrictionMessage: "You cannot submit at this time"
    };
  }
  return {
    canOpen: true,
    label: "Submit Work to The Memes",
    compactLabel: "Submit",
    restrictionMessage: null
  };
};
const MyStreamWaveContent: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(183);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    setWaveDropAction
  } = useHeaderContext();
  const {
    waves,
    directMessages,
    registerWave
  } = useMyStream();
  const {
    updateEligibility
  } = useWaveEligibility();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  let t4;
  if ($[8] !== registerWave || $[9] !== waveId) {
    t3 = () => {
      registerWave(waveId, true);
    };
    t4 = [registerWave, waveId];
    $[8] = registerWave;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== updateEligibility || $[13] !== wave) {
    t5 = () => {
      if (!wave) {
        return;
      }
      updateEligibility(wave.id, {
        authenticated_user_eligible_to_chat: wave.chat.authenticated_user_eligible,
        authenticated_user_eligible_to_vote: wave.voting.authenticated_user_eligible,
        authenticated_user_eligible_to_participate: wave.participation.authenticated_user_eligible,
        authenticated_user_admin: wave.wave.authenticated_user_eligible_for_admin
      });
    };
    t6 = [updateEligibility, wave];
    $[12] = updateEligibility;
    $[13] = wave;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== directMessages.list || $[17] !== waveId || $[18] !== waves.list) {
    t7 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[16] = directMessages.list;
    $[17] = waveId;
    $[18] = waves.list;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const waveFromList = t7;
  const t8 = waveFromList?.newDropsCount.count ?? 0;
  const t9 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      newDropsCount: t8,
      firstUnreadSerialNo: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const enhancedData = t10;
  const newDropsCount = enhancedData.newDropsCount;
  let t11;
  if ($[23] !== newDropsCount || $[24] !== wave) {
    t11 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[23] = newDropsCount;
    $[24] = wave;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  useSetWaveData(t11);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  let t12;
  if ($[26] !== searchParams) {
    t12 = searchParams.get("curation");
    $[26] = searchParams;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const activeCurationId = t12;
  const loadedWaveId = wave?.id ?? null;
  const editingDropId = useSelector(selectEditingDropId);
  const {
    viewMode,
    setViewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    isDm,
    isChatWave,
    participation
  } = useWave(wave);
  const {
    canSubmitNow: participationCanSubmitNow,
    endTime: participationEndTime,
    hasReachedLimit: participationHasReachedLimit,
    isEligible: participationIsEligible,
    maxSubmissions: participationMaxSubmissions,
    startTime: participationStartTime,
    status: participationStatus
  } = participation;
  let t13;
  if ($[28] !== wave) {
    t13 = {
      wave
    };
    $[28] = wave;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const {
    isVotingControlsLocked: isApprovalVotingControlsLocked
  } = useApprovalWaveStatus(t13);
  const showGalleryToggle = !isRankWave && !isApproveWave && !isMemesWave && !isDm;
  let t14;
  if ($[30] !== searchParams) {
    t14 = searchParams.get("serialNo");
    $[30] = searchParams;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const hasSerialTarget = t14 !== null;
  wave?.participation.submission_strategy;
  const t15 = wave?.participation.submission_strategy ?? null;
  let t16;
  if ($[32] !== isCurationWave || $[33] !== isMemesWave || $[34] !== isQuorumWave || $[35] !== t15) {
    t16 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t15
    });
    $[32] = isCurationWave;
    $[33] = isMemesWave;
    $[34] = isQuorumWave;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const submissionExperience = t16;
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t17 = Boolean(activeProfileProxy);
  let t18;
  if ($[37] !== isCurationWave || $[38] !== isLoggedIn || $[39] !== participation || $[40] !== t17) {
    t18 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t17,
      isCurationWave,
      participation
    });
    $[37] = isCurationWave;
    $[38] = isLoggedIn;
    $[39] = participation;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const dropEligibility = t18;
  let t19;
  if ($[42] !== dropEligibility || $[43] !== isApprovalVotingControlsLocked) {
    t19 = getChatSubmitDropRestrictionMessage({
      dropEligibility,
      isApprovalVotingControlsLocked
    });
    $[42] = dropEligibility;
    $[43] = isApprovalVotingControlsLocked;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const chatSubmitDropRestrictionMessage = t19;
  const canOpenChatSubmitDrop = dropEligibility.canCreateDrop && !isApprovalVotingControlsLocked;
  const showChatSubmitDropAction = !isChatWave && !isMemesWave && submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY;
  let t20;
  if ($[45] !== submissionExperience) {
    t20 = getChatSubmitDropLabels(submissionExperience);
    $[45] = submissionExperience;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const chatSubmitDropLabels = t20;
  const isMemesLegacySubmission = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY;
  const [chatSubmitDropState, setChatSubmitDropState] = useState(null);
  const [appMemesSubmitWaveId, setAppMemesSubmitWaveId] = useState(null);
  let t21;
  let t22;
  if ($[47] !== hasSerialTarget || $[48] !== setViewMode || $[49] !== showGalleryToggle || $[50] !== viewMode || $[51] !== wave) {
    t21 = () => {
      if (!wave || !hasSerialTarget || !showGalleryToggle || viewMode !== "gallery") {
        return;
      }
      setViewMode("chat");
    };
    t22 = [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave];
    $[47] = hasSerialTarget;
    $[48] = setViewMode;
    $[49] = showGalleryToggle;
    $[50] = viewMode;
    $[51] = wave;
    $[52] = t21;
    $[53] = t22;
  } else {
    t21 = $[52];
    t22 = $[53];
  }
  useEffect(t21, t22);
  useBreakpoint();
  let t23;
  if ($[54] !== pathname || $[55] !== queryClient || $[56] !== router || $[57] !== searchParams) {
    t23 = drop => {
      queryClient.setQueryData(getDropQueryKey(drop.id), drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[54] = pathname;
    $[55] = queryClient;
    $[56] = router;
    $[57] = searchParams;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const onDropClick = t23;
  let t24;
  if ($[59] !== canOpenChatSubmitDrop || $[60] !== isApprovalVotingControlsLocked || $[61] !== showChatSubmitDropAction || $[62] !== submissionExperience || $[63] !== wave) {
    t24 = t25 => {
      const initialCurationUrl = t25 === undefined ? null : t25;
      if (!wave || !showChatSubmitDropAction || !canOpenChatSubmitDrop) {
        return;
      }
      setChatSubmitDropState({
        waveId: wave.id,
        submissionExperience,
        initialCurationUrl,
        isApprovalVotingControlsLocked
      });
    };
    $[59] = canOpenChatSubmitDrop;
    $[60] = isApprovalVotingControlsLocked;
    $[61] = showChatSubmitDropAction;
    $[62] = submissionExperience;
    $[63] = wave;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  const openChatSubmitDrop = t24;
  let t25;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      setChatSubmitDropState(null);
    };
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const closeChatSubmitDrop = t25;
  let t26;
  if ($[66] !== isApprovalVotingControlsLocked || $[67] !== participationCanSubmitNow || $[68] !== participationEndTime || $[69] !== participationHasReachedLimit || $[70] !== participationIsEligible || $[71] !== participationMaxSubmissions || $[72] !== participationStartTime || $[73] !== participationStatus) {
    t26 = getMemesHeaderDropActionState({
      participationState: {
        canSubmitNow: participationCanSubmitNow,
        endTime: participationEndTime,
        hasReachedLimit: participationHasReachedLimit,
        isEligible: participationIsEligible,
        maxSubmissions: participationMaxSubmissions,
        startTime: participationStartTime,
        status: participationStatus
      },
      isApprovalVotingControlsLocked
    });
    $[66] = isApprovalVotingControlsLocked;
    $[67] = participationCanSubmitNow;
    $[68] = participationEndTime;
    $[69] = participationHasReachedLimit;
    $[70] = participationIsEligible;
    $[71] = participationMaxSubmissions;
    $[72] = participationStartTime;
    $[73] = participationStatus;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  const memesHeaderDropActionState = t26;
  const canOpenAppMemesSubmit = memesHeaderDropActionState.canOpen;
  let t27;
  if ($[75] !== canOpenAppMemesSubmit || $[76] !== isMemesLegacySubmission || $[77] !== loadedWaveId) {
    t27 = () => {
      if (!loadedWaveId || !isMemesLegacySubmission || !canOpenAppMemesSubmit) {
        return;
      }
      setAppMemesSubmitWaveId(loadedWaveId);
    };
    $[75] = canOpenAppMemesSubmit;
    $[76] = isMemesLegacySubmission;
    $[77] = loadedWaveId;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  const openAppMemesSubmit = t27;
  let t28;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setAppMemesSubmitWaveId(null);
    };
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  const closeAppMemesSubmit = t28;
  let t29;
  if ($[80] !== openChatSubmitDrop) {
    t29 = () => openChatSubmitDrop(null);
    $[80] = openChatSubmitDrop;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== canOpenChatSubmitDrop || $[83] !== chatSubmitDropLabels.compactLabel || $[84] !== chatSubmitDropLabels.label || $[85] !== chatSubmitDropRestrictionMessage || $[86] !== openChatSubmitDrop || $[87] !== showChatSubmitDropAction || $[88] !== t29) {
    t30 = {
      isVisible: showChatSubmitDropAction,
      canOpen: canOpenChatSubmitDrop,
      label: chatSubmitDropLabels.label,
      compactLabel: chatSubmitDropLabels.compactLabel,
      restrictionMessage: chatSubmitDropRestrictionMessage,
      onOpen: t29,
      onOpenWithCurationUrl: openChatSubmitDrop
    };
    $[82] = canOpenChatSubmitDrop;
    $[83] = chatSubmitDropLabels.compactLabel;
    $[84] = chatSubmitDropLabels.label;
    $[85] = chatSubmitDropRestrictionMessage;
    $[86] = openChatSubmitDrop;
    $[87] = showChatSubmitDropAction;
    $[88] = t29;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  const chatSubmitDropAction = t30;
  let t31;
  bb0: {
    if (!isApp || !loadedWaveId || editingDropId !== null || activeContentTab !== MyStreamWaveTab.CHAT || activeCurationId !== null || !isMemesLegacySubmission && !chatSubmitDropAction.isVisible) {
      t31 = null;
      break bb0;
    }
    if (isMemesLegacySubmission) {
      let t32;
      if ($[90] !== loadedWaveId || $[91] !== memesHeaderDropActionState.canOpen || $[92] !== memesHeaderDropActionState.compactLabel || $[93] !== memesHeaderDropActionState.label || $[94] !== memesHeaderDropActionState.restrictionMessage || $[95] !== openAppMemesSubmit) {
        t32 = {
          waveId: loadedWaveId,
          canOpen: memesHeaderDropActionState.canOpen,
          label: memesHeaderDropActionState.label,
          compactLabel: memesHeaderDropActionState.compactLabel,
          restrictionMessage: memesHeaderDropActionState.restrictionMessage,
          onOpen: openAppMemesSubmit
        };
        $[90] = loadedWaveId;
        $[91] = memesHeaderDropActionState.canOpen;
        $[92] = memesHeaderDropActionState.compactLabel;
        $[93] = memesHeaderDropActionState.label;
        $[94] = memesHeaderDropActionState.restrictionMessage;
        $[95] = openAppMemesSubmit;
        $[96] = t32;
      } else {
        t32 = $[96];
      }
      t31 = t32;
      break bb0;
    }
    let t32;
    if ($[97] !== chatSubmitDropAction.canOpen || $[98] !== chatSubmitDropAction.compactLabel || $[99] !== chatSubmitDropAction.label || $[100] !== chatSubmitDropAction.onOpen || $[101] !== chatSubmitDropAction.restrictionMessage || $[102] !== loadedWaveId) {
      t32 = {
        waveId: loadedWaveId,
        canOpen: chatSubmitDropAction.canOpen,
        label: chatSubmitDropAction.label,
        compactLabel: chatSubmitDropAction.compactLabel,
        restrictionMessage: chatSubmitDropAction.restrictionMessage,
        onOpen: chatSubmitDropAction.onOpen
      };
      $[97] = chatSubmitDropAction.canOpen;
      $[98] = chatSubmitDropAction.compactLabel;
      $[99] = chatSubmitDropAction.label;
      $[100] = chatSubmitDropAction.onOpen;
      $[101] = chatSubmitDropAction.restrictionMessage;
      $[102] = loadedWaveId;
      $[103] = t32;
    } else {
      t32 = $[103];
    }
    t31 = t32;
  }
  const headerWaveDropAction = t31;
  let t32;
  let t33;
  if ($[104] !== headerWaveDropAction || $[105] !== setWaveDropAction) {
    t32 = () => {
      setWaveDropAction(headerWaveDropAction);
      return () => setWaveDropAction(null);
    };
    t33 = [headerWaveDropAction, setWaveDropAction];
    $[104] = headerWaveDropAction;
    $[105] = setWaveDropAction;
    $[106] = t32;
    $[107] = t33;
  } else {
    t32 = $[106];
    t33 = $[107];
  }
  useEffect(t32, t33);
  let t34;
  if ($[108] !== pathname || $[109] !== router || $[110] !== searchParams) {
    t34 = curationId => {
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params_1.set("curation", curationId);
      } else {
        params_1.delete("curation");
      }
      const nextQuery = params_1.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      const currentQuery = searchParams.toString();
      const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
      if (nextUrl === currentUrl) {
        return;
      }
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[108] = pathname;
    $[109] = router;
    $[110] = searchParams;
    $[111] = t34;
  } else {
    t34 = $[111];
  }
  const onSelectCuration = t34;
  if (!wave) {
    return null;
  }
  let t35;
  if ($[112] !== chatSubmitDropState || $[113] !== isApprovalVotingControlsLocked || $[114] !== submissionExperience || $[115] !== wave.id) {
    t35 = chatSubmitDropState?.waveId === wave.id && chatSubmitDropState.submissionExperience === submissionExperience && chatSubmitDropState.isApprovalVotingControlsLocked === isApprovalVotingControlsLocked ? {
      submissionExperience: chatSubmitDropState.submissionExperience,
      initialCurationUrl: chatSubmitDropState.initialCurationUrl
    } : null;
    $[112] = chatSubmitDropState;
    $[113] = isApprovalVotingControlsLocked;
    $[114] = submissionExperience;
    $[115] = wave.id;
    $[116] = t35;
  } else {
    t35 = $[116];
  }
  const activeChatSubmitDropState = t35;
  const isAppMemesSubmitModalOpen = appMemesSubmitWaveId === wave.id && isMemesLegacySubmission && !isApprovalVotingControlsLocked;
  const t36 = showGalleryToggle ? viewMode : "chat";
  let t37;
  if ($[117] !== activeChatSubmitDropState || $[118] !== chatSubmitDropAction || $[119] !== enhancedData.firstUnreadSerialNo || $[120] !== onDropClick || $[121] !== t36 || $[122] !== wave) {
    t37 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t36} onDropClick={onDropClick} chatSubmitDrop={activeChatSubmitDropState} chatSubmitDropAction={chatSubmitDropAction} onCloseChatSubmitDrop={closeChatSubmitDrop} />;
    $[117] = activeChatSubmitDropState;
    $[118] = chatSubmitDropAction;
    $[119] = enhancedData.firstUnreadSerialNo;
    $[120] = onDropClick;
    $[121] = t36;
    $[122] = wave;
    $[123] = t37;
  } else {
    t37 = $[123];
  }
  let t38;
  if ($[124] !== onDropClick || $[125] !== wave) {
    t38 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[124] = onDropClick;
    $[125] = wave;
    $[126] = t38;
  } else {
    t38 = $[126];
  }
  let t39;
  if ($[127] !== onDropClick || $[128] !== wave) {
    t39 = <MyStreamWaveSubmissions wave={wave} onDropClick={onDropClick} />;
    $[127] = onDropClick;
    $[128] = wave;
    $[129] = t39;
  } else {
    t39 = $[129];
  }
  let t40;
  if ($[130] !== wave.id) {
    t40 = <MyStreamWaveSales waveId={wave.id} />;
    $[130] = wave.id;
    $[131] = t40;
  } else {
    t40 = $[131];
  }
  let t41;
  if ($[132] !== onDropClick || $[133] !== wave) {
    t41 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[132] = onDropClick;
    $[133] = wave;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  let t42;
  if ($[135] !== wave) {
    t42 = <MyStreamWaveOutcome wave={wave} />;
    $[135] = wave;
    $[136] = t42;
  } else {
    t42 = $[136];
  }
  let t43;
  if ($[137] !== onDropClick || $[138] !== wave) {
    t43 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[137] = onDropClick;
    $[138] = wave;
    $[139] = t43;
  } else {
    t43 = $[139];
  }
  let t44;
  if ($[140] !== onDropClick || $[141] !== wave) {
    t44 = <MyStreamWavePolls wave={wave} onDropClick={onDropClick} />;
    $[140] = onDropClick;
    $[141] = wave;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  let t45;
  if ($[143] !== wave) {
    t45 = <MyStreamWaveFAQ wave={wave} />;
    $[143] = wave;
    $[144] = t45;
  } else {
    t45 = $[144];
  }
  let t46;
  if ($[145] !== t37 || $[146] !== t38 || $[147] !== t39 || $[148] !== t40 || $[149] !== t41 || $[150] !== t42 || $[151] !== t43 || $[152] !== t44 || $[153] !== t45) {
    t46 = {
      [MyStreamWaveTab.CHAT]: t37,
      [MyStreamWaveTab.LEADERBOARD]: t38,
      [MyStreamWaveTab.SUBMISSIONS]: t39,
      [MyStreamWaveTab.SALES]: t40,
      [MyStreamWaveTab.WINNERS]: t41,
      [MyStreamWaveTab.OUTCOME]: t42,
      [MyStreamWaveTab.MY_VOTES]: t43,
      [MyStreamWaveTab.POLLS]: t44,
      [MyStreamWaveTab.FAQ]: t45
    };
    $[145] = t37;
    $[146] = t38;
    $[147] = t39;
    $[148] = t40;
    $[149] = t41;
    $[150] = t42;
    $[151] = t43;
    $[152] = t44;
    $[153] = t45;
    $[154] = t46;
  } else {
    t46 = $[154];
  }
  const components = t46;
  let t47;
  if ($[155] !== activeCurationId || $[156] !== chatSubmitDropAction || $[157] !== onSelectCuration || $[158] !== showGalleryToggle || $[159] !== toggleViewMode || $[160] !== viewMode || $[161] !== wave) {
    t47 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} chatSubmitDropAction={chatSubmitDropAction} />;
    $[155] = activeCurationId;
    $[156] = chatSubmitDropAction;
    $[157] = onSelectCuration;
    $[158] = showGalleryToggle;
    $[159] = toggleViewMode;
    $[160] = viewMode;
    $[161] = wave;
    $[162] = t47;
  } else {
    t47 = $[162];
  }
  let t48;
  if ($[163] !== activeContentTab || $[164] !== activeCurationId) {
    t48 = activeCurationId ? `my-stream-wave-tabpanel-curation-${activeCurationId}` : getContentTabPanelId(activeContentTab);
    $[163] = activeContentTab;
    $[164] = activeCurationId;
    $[165] = t48;
  } else {
    t48 = $[165];
  }
  let t49;
  if ($[166] !== activeContentTab || $[167] !== activeCurationId || $[168] !== components || $[169] !== onDropClick || $[170] !== wave) {
    t49 = activeCurationId ? <MyStreamWaveCurationContent key={activeCurationId} wave={wave} curationId={activeCurationId} onDropClick={onDropClick} /> : components[activeContentTab];
    $[166] = activeContentTab;
    $[167] = activeCurationId;
    $[168] = components;
    $[169] = onDropClick;
    $[170] = wave;
    $[171] = t49;
  } else {
    t49 = $[171];
  }
  let t50;
  if ($[172] !== t48 || $[173] !== t49) {
    t50 = <div className="tw-relative tw-min-h-0 tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t48}>{t49}</div>;
    $[172] = t48;
    $[173] = t49;
    $[174] = t50;
  } else {
    t50 = $[174];
  }
  let t51;
  if ($[175] !== isAppMemesSubmitModalOpen || $[176] !== wave) {
    t51 = <MemesArtSubmissionModal isOpen={isAppMemesSubmitModalOpen} wave={wave} onClose={closeAppMemesSubmit} />;
    $[175] = isAppMemesSubmitModalOpen;
    $[176] = wave;
    $[177] = t51;
  } else {
    t51 = $[177];
  }
  let t52;
  if ($[178] !== stableWaveKey || $[179] !== t47 || $[180] !== t50 || $[181] !== t51) {
    t52 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t47}{t50}{t51}</div>;
    $[178] = stableWaveKey;
    $[179] = t47;
    $[180] = t50;
    $[181] = t51;
    $[182] = t52;
  } else {
    t52 = $[182];
  }
  return t52;
};
export default MyStreamWaveContent;